// Catalogue loader. Fetches categories/brands/products/inventory once (in
// parallel) and derives the lookup maps the storefront needs, cached at module
// scope so navigating between the shop grid and a product page never refetches.
//
// BUG-522: product image links are signed S3 URLs that expire after 15 minutes
// (webshop `shop.storage.signed-url-ttl`). A cache kept longer than that showed
// blank image frames in the basket and the checkout summary, so the cache has a
// max age: a view that mounts on an older catalogue, or a tab that becomes
// visible again, reloads it in the background (the old data stays on screen),
// and every mounted view gets the new links.

import { useCallback, useEffect, useState } from 'react'
import { listProducts, listCategories, listBrands, listInventory } from './shopApi'
import type { Product, Category, Brand, InventoryItem } from './types'

export interface Catalogue {
  products: Product[]
  categories: Category[]
  brands: Brand[]
  inventory: InventoryItem[]
  /** InventoryItem.available, keyed by SKU. */
  stockBySku: Record<string, number>
  categoryById: Record<string, Category>
  brandById: Record<string, Brand>
}

const EMPTY: Catalogue = {
  products: [],
  categories: [],
  brands: [],
  inventory: [],
  stockBySku: {},
  categoryById: {},
  brandById: {},
}

/** Reload once the catalogue is this old: 5 minutes inside the 15-minute image link lifetime. */
export const CATALOGUE_MAX_AGE_MS = 10 * 60 * 1000

let cache: Catalogue | null = null
let inflight: Promise<Catalogue> | null = null
/** When `cache` was loaded (Date.now()). */
let loadedAt = 0
/** Every mounted useCatalogue, so a reload started anywhere reaches them all. */
const listeners = new Set<(c: Catalogue) => void>()

function derive(
  products: Product[],
  categories: Category[],
  brands: Brand[],
  inventory: InventoryItem[],
): Catalogue {
  return {
    products,
    categories,
    brands,
    inventory,
    stockBySku: Object.fromEntries(inventory.map((i) => [i.sku, i.available])),
    categoryById: Object.fromEntries(categories.map((c) => [c.id, c])),
    brandById: Object.fromEntries(brands.map((b) => [b.id, b])),
  }
}

async function load(): Promise<Catalogue> {
  const [products, categories, brands, inventory] = await Promise.all([
    listProducts(),
    listCategories(),
    listBrands(),
    listInventory(),
  ])
  const next = derive(products, categories, brands, inventory)
  cache = next
  loadedAt = Date.now()
  listeners.forEach((l) => l(next))
  return next
}

function ensureLoad(force: boolean): Promise<Catalogue> {
  // Defensive: useCatalogue's effect already returns early on a warm cache, so
  // no current caller reaches this with `cache && !force`. Kept for any future
  // caller; excluded from coverage because no test can reach it through the hook.
  /* v8 ignore next */
  if (cache && !force) return Promise.resolve(cache)
  if (!inflight || force) {
    inflight = load().finally(() => {
      inflight = null
    })
  }
  return inflight
}

/** Force the next `useCatalogue()` call to refetch (e.g. after stock changes). */
export function invalidateCatalogue() {
  cache = null
  inflight = null
  loadedAt = 0
}

/**
 * Background reload when the loaded catalogue is at least `minAgeMs` old (one request shared by all
 * callers). Never rejects: on failure the current data stays. Does nothing before the first load,
 * which useCatalogue owns.
 */
export function refreshCatalogue(minAgeMs: number): Promise<void> {
  if (!cache || Date.now() - loadedAt < minAgeMs) return Promise.resolve()
  if (!inflight) {
    inflight = load().finally(() => {
      inflight = null
    })
  }
  return inflight.then(
    () => undefined,
    () => undefined,
  )
}

export function useCatalogue() {
  const [data, setData] = useState<Catalogue>(cache ?? EMPTY)
  const [phase, setPhase] = useState<'loading' | 'ready' | 'error'>(cache ? 'ready' : 'loading')
  const [reloadNonce, setReloadNonce] = useState(0)

  const reload = useCallback(() => {
    cache = null
    setPhase('loading')
    setReloadNonce((n) => n + 1)
  }, [])

  // Any load (this view's, another view's, or a background refresh) updates this view.
  useEffect(() => {
    const onLoad = (c: Catalogue) => {
      setData(c)
      setPhase('ready')
    }
    listeners.add(onLoad)
    const refreshIfStale = () => {
      if (document.visibilityState !== 'hidden') void refreshCatalogue(CATALOGUE_MAX_AGE_MS)
    }
    refreshIfStale()
    document.addEventListener('visibilitychange', refreshIfStale)
    return () => {
      listeners.delete(onLoad)
      document.removeEventListener('visibilitychange', refreshIfStale)
    }
  }, [])

  useEffect(() => {
    if (cache && reloadNonce === 0) return
    let cancelled = false
    ensureLoad(reloadNonce > 0)
      .then((c) => {
        if (cancelled) return
        setData(c)
        setPhase('ready')
      })
      .catch(() => {
        if (!cancelled) setPhase('error')
      })
    return () => {
      cancelled = true
    }
  }, [reloadNonce])

  return { ...data, phase, reload }
}
